/**
 * useLogoSettings
 * Returns the logo URL for the current context with fallback: branch → org → platform → default.
 * Theme-aware (light/dark variants).
 */
import { BRANCH_SETTINGS_PERMISSIONS } from '@/config/permissions/submenuPermissionKeys'


import { useCallback, useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useAuth } from '@/hooks/useAuth'
import { usePermissions } from '@/hooks/usePermissions'
import { useTheme } from '@/lib/theme'
import { settingService } from '@/services/api/platform/setting.service'
import { organizationSettingService } from '@/services/api/organization/setting.service'
import { branchSettingService } from '@/services/api/branch/setting.service'
import { getCacheConfig } from '@/config/caching.config'
import { settingsQueryKeys } from '@/config/settings-react-query.keys'
import {
  DEFAULT_LOGO_DARK_HREF,
  DEFAULT_LOGO_LIGHT_HREF,
  getDefaultLogoHref,
} from '@/config/branding.config'
import type { Setting } from '@/types/platform/setting.types'
import type { WorkspaceBranding } from '@/types/shared/auth.types'
import { getPublicFileUrl } from '@/utils/storage-public-url.util'

/** Shown in UI next to the mark; matches `ContextData['contextType']`. */
export type LogoScopeKey = 'platform' | 'organization' | 'branch'

/** Two letters from slug (alphanumeric only); fixed fallbacks if empty. */
export function initialsFromSlug(
  slug: string | undefined,
  /** e.g. "BR", "OR", "VI" when slug missing */
  fallback: string
): string {
  const letters = (slug ?? '').replace(/[^a-zA-Z0-9]/g, '')
  if (letters.length >= 2) return letters.slice(0, 2).toUpperCase()
  if (letters.length === 1) return (letters + letters).toUpperCase()
  return fallback
}

function getSettingValue(settings: Setting[] | undefined, key: string): string | undefined {
  if (!settings || !Array.isArray(settings)) return undefined
  const s = settings.find((x) => x.key === key)
  const v = s?.value
  if (v === undefined || v === null || v === '') return undefined
  return normalizeLogoUrl(String(v).trim())
}

/** Full CDN URL for settings values stored as keys or absolute URLs. */
function normalizeLogoUrl(url: string | undefined | null): string | undefined {
  if (!url?.trim()) return undefined
  const trimmed = url.trim()
  if (/^https?:\/\//i.test(trimmed) || trimmed.startsWith('data:')) return trimmed
  return getPublicFileUrl(trimmed)
}

function pickFirstLogoUrl(...candidates: Array<string | null | undefined>): string | undefined {
  for (const candidate of candidates) {
    const normalized = normalizeLogoUrl(candidate ?? undefined)
    if (normalized) return normalized
  }
  return undefined
}

function branchLogoFromBootstrap(branding: WorkspaceBranding, isDark: boolean): string | undefined {
  if (isDark) {
    return pickFirstLogoUrl(
      branding.branchLogoDark,
      branding.branchLogo,
      branding.resolvedLogoDark,
      branding.resolvedLogo
    )
  }
  return pickFirstLogoUrl(
    branding.branchLogo,
    branding.branchLogoDark,
    branding.resolvedLogo,
    branding.resolvedLogoDark
  )
}

/**
 * Add cache-busting parameter to logo URL to force browser refresh
 * Uses the most recent updatedAt timestamp from the settings array
 */
function addCacheBuster(url: string, settings: Setting[] | undefined): string {
  if (!url) return url
  if (!settings || settings.length === 0) return url

  // Find the most recent updatedAt timestamp from logo-related settings
  const logoSettings = settings.filter(s => 
    s.key?.includes('logo') && s.updatedAt
  )
  
  if (logoSettings.length === 0) return url

  // Get the latest timestamp
  const latestTimestamp = logoSettings.reduce((latest, setting) => {
    const timestamp = new Date(setting.updatedAt!).getTime()
    return timestamp > latest ? timestamp : latest
  }, 0)

  // Add version parameter
  const separator = url.includes('?') ? '&' : '?'
  return `${url}${separator}v=${latestTimestamp}`
}

/**
 * Returns logo URL for authenticated UI (branch → org → public branding per context; platform from platform settings only).
 * If nothing is configured, `logoUrl` is the default Vidhyantra light/dark logo.
 * height/maxWidth are unused; kept for API compatibility.
 */
export function useLogoSettings(): {
  /** Resolved image from settings/chain, or the default brand logo. */
  logoUrl: string | null
  slugInitials: string
  scopeKey: LogoScopeKey
  isLoading: boolean
  height?: number
  maxWidth?: number
} {
  const { contextData } = useAuth()
  const { hasPermission } = usePermissions()
  const { getEffectiveMode } = useTheme()
  const cacheConfig = getCacheConfig('settings.list')
  const organizationId = contextData?.organizationId
  const branchId = contextData?.branchId
  const isPlatformContext = contextData?.contextType === 'platform'
  const canFetchOrgBranding =
    contextData?.contextType === 'organization' &&
    !!contextData?.organizationId &&
    hasPermission('organization.organizationsettings.view')
  const canFetchBranchBranding =
    contextData?.contextType === 'branch' &&
    !!contextData?.branchId &&
    hasPermission(BRANCH_SETTINGS_PERMISSIONS.general)

  const bootstrapBranding = contextData?.branding

  const needPublicBrandingFallback =
    !isPlatformContext &&
    (contextData?.contextType === 'organization' || contextData?.contextType === 'branch')

  const { data: platformSettings = [], isLoading: platformLoading } = useQuery({
    queryKey: settingsQueryKeys.platformBranding(),
    queryFn: () => settingService.getSettings({ scope: 'PLATFORM', category: 'branding' }),
    select: (data) => (Array.isArray(data) ? data : []),
    enabled: isPlatformContext,
    staleTime: cacheConfig.frontendTTL,
    gcTime: cacheConfig.frontendTTL * 2,
    refetchOnMount: false,
  })

  const { data: orgSettings = [], isLoading: orgLoading } = useQuery({
    queryKey: settingsQueryKeys.organizationBranding(organizationId),
    queryFn: () => organizationSettingService.getSettings({ category: 'branding' }),
    select: (data) => (Array.isArray(data) ? data : []),
    enabled: canFetchOrgBranding && !!organizationId,
    staleTime: cacheConfig.frontendTTL,
    gcTime: cacheConfig.frontendTTL * 2,
    refetchOnMount: false,
  })

  const { data: branchSettings = [], isLoading: branchLoading } = useQuery({
    queryKey: settingsQueryKeys.branchBrandingInherited(branchId),
    queryFn: () => branchSettingService.getSettingsWithInheritance({ category: 'branding' }),
    select: (data) => (Array.isArray(data) ? data : []),
    enabled: canFetchBranchBranding && !!branchId,
    staleTime: cacheConfig.frontendTTL,
    gcTime: cacheConfig.frontendTTL * 2,
    refetchOnMount: 'always',
  })

  const { data: publicBranding, isLoading: publicBrandingLoading } = useQuery({
    queryKey: settingsQueryKeys.publicBranding(),
    queryFn: () => settingService.getPublicBranding(),
    staleTime: cacheConfig.frontendTTL,
    gcTime: cacheConfig.frontendTTL * 2,
    refetchOnMount: false,
    enabled: needPublicBrandingFallback,
  })

  const logoUrl = useMemo((): string | null => {
    const isDark = getEffectiveMode() === 'dark'
    const branchKey = isDark ? 'branch_logo_dark_url' : 'branch_logo_url'
    const orgKey = isDark ? 'org_logo_dark_url' : 'org_logo_url'
    const platformKey = isDark ? 'platform_logo_dark_url' : 'platform_logo_url'

    let baseUrl: string | undefined

    // 1. Branch logo (if in branch context)
    if (contextData?.contextType === 'branch') {
      // Workspace bootstrap branding is available to all branch users (no settings permission).
      if (bootstrapBranding) {
        baseUrl = branchLogoFromBootstrap(bootstrapBranding, isDark)
        if (baseUrl) return baseUrl
      }

      baseUrl =
        getSettingValue(branchSettings, branchKey) ||
        getSettingValue(branchSettings, 'branch_logo_url') ||
        getSettingValue(branchSettings, 'branch_logo')
      if (baseUrl) {
        return addCacheBuster(baseUrl, branchSettings)
      }
      
      // 2. Inherited organization logo (from branch settings with inheritance)
      baseUrl =
        getSettingValue(branchSettings, orgKey) || getSettingValue(branchSettings, 'org_logo_url')
      if (baseUrl) {
        return addCacheBuster(baseUrl, branchSettings)
      }

      if (bootstrapBranding) {
        baseUrl = isDark
          ? pickFirstLogoUrl(
              bootstrapBranding.orgLogoDark,
              bootstrapBranding.orgLogo,
              bootstrapBranding.platformLogoDark,
              bootstrapBranding.platformLogo
            )
          : pickFirstLogoUrl(bootstrapBranding.orgLogo, bootstrapBranding.platformLogo)
        if (baseUrl) return baseUrl
      }

      // 3. Platform (public branding) when branch + org have no logo
      if (publicBranding) {
        baseUrl = isDark
          ? pickFirstLogoUrl(publicBranding.logoDarkUrl, publicBranding.logoUrl)
          : pickFirstLogoUrl(publicBranding.logoUrl)
        if (baseUrl) return baseUrl
      }
    }

    // Organization logo (org context)
    if (contextData?.contextType === 'organization' && contextData?.organizationId) {
      baseUrl =
        getSettingValue(orgSettings, orgKey) || getSettingValue(orgSettings, 'org_logo_url')
      if (baseUrl) {
        return addCacheBuster(baseUrl, orgSettings)
      }

      if (bootstrapBranding) {
        baseUrl = isDark
          ? bootstrapBranding.orgLogoDark ||
            bootstrapBranding.orgLogo ||
            bootstrapBranding.resolvedLogoDark ||
            bootstrapBranding.resolvedLogo ||
            undefined
          : bootstrapBranding.orgLogo || bootstrapBranding.resolvedLogo || undefined
        if (baseUrl) return baseUrl
      }

      if (publicBranding) {
        baseUrl =
          (isDark ? publicBranding.logoDarkUrl : null) ||
          publicBranding.logoUrl ||
          undefined
        if (baseUrl) return baseUrl
      }
    }

    // Platform logo (platform admin context)
    if (isPlatformContext) {
      baseUrl =
        getSettingValue(platformSettings, platformKey) ||
        getSettingValue(platformSettings, 'platform_logo_url')
      if (baseUrl) {
        return addCacheBuster(baseUrl, platformSettings)
      }
    }

    // 4. Fallback to light logos when dark variant not set
    if (isDark) {
      if (contextData?.contextType === 'branch') {
        const branchLight = getSettingValue(branchSettings, 'branch_logo_url') || getSettingValue(branchSettings, 'branch_logo')
        if (branchLight) {
          return addCacheBuster(branchLight, branchSettings)
        }
        const orgLight = getSettingValue(branchSettings, 'org_logo_url')
        if (orgLight) {
          return addCacheBuster(orgLight, branchSettings)
        }
      }
      if (contextData?.contextType === 'organization') {
        const orgLight = getSettingValue(orgSettings, 'org_logo_url')
        if (orgLight) {
          return addCacheBuster(orgLight, orgSettings)
        }
      }
      if (isPlatformContext) {
        const platformLight = getSettingValue(platformSettings, 'platform_logo_url')
        if (platformLight) {
          return addCacheBuster(platformLight, platformSettings)
        }
      }
      if (
        (contextData?.contextType === 'branch' || contextData?.contextType === 'organization') &&
        publicBranding?.logoUrl
      ) {
        return publicBranding.logoUrl
      }
    }

    return getDefaultLogoHref(isDark ? 'dark' : 'light')
  }, [
    contextData?.contextType,
    contextData?.organizationId,
    contextData?.branchId,
    bootstrapBranding,
    isPlatformContext,
    branchSettings,
    orgSettings,
    platformSettings,
    publicBranding,
    getEffectiveMode,
  ])

  const scopeKey: LogoScopeKey = contextData?.contextType ?? 'platform'

  const slugInitials = useMemo(() => {
    if (scopeKey === 'branch') {
      return initialsFromSlug(
        contextData?.branchSlug || contextData?.organizationSlug,
        'BR'
      )
    }
    if (scopeKey === 'organization') {
      return initialsFromSlug(contextData?.organizationSlug, 'OR')
    }
    return initialsFromSlug(undefined, 'VI')
  }, [
    scopeKey,
    contextData?.branchSlug,
    contextData?.organizationSlug,
  ])

  const isLoading =
    (isPlatformContext && platformLoading) ||
    (contextData?.contextType === 'organization' && canFetchOrgBranding && orgLoading) ||
    (contextData?.contextType === 'branch' && canFetchBranchBranding && branchLoading) ||
    (needPublicBrandingFallback && publicBrandingLoading)

  return {
    logoUrl,
    slugInitials,
    scopeKey,
    isLoading,
    height: undefined,
    maxWidth: undefined,
  }
}

export type PlatformLogoSurface = 'auto' | 'navbar' | 'footer'

/**
 * Returns platform logo URL only (for PublicNavbar / login / landing pages).
 * Settings override the default; missing or failed images fall back to the brand logo.
 * Navbar / light theme → light logo. Footer / dark theme → dark logo.
 */
export function usePlatformLogoSettings(options?: {
  surface?: PlatformLogoSurface
}): { logoUrl: string; fallbackUrl: string; isLoading: boolean } {
  const { getEffectiveMode } = useTheme()
  const cacheConfig = getCacheConfig('settings.list')
  const surface = options?.surface ?? 'auto'

  const { data: branding, isLoading } = useQuery({
    queryKey: settingsQueryKeys.publicBranding(),
    queryFn: () => settingService.getPublicBranding(),
    staleTime: cacheConfig.frontendTTL,
    gcTime: cacheConfig.frontendTTL * 2,
    refetchOnMount: false,
  })

  const { logoUrl, fallbackUrl } = useMemo(() => {
    const themeDark = getEffectiveMode() === 'dark'
    const useDark =
      surface === 'footer' || (surface === 'auto' && themeDark) || (surface === 'navbar' && themeDark)
    const fallback = useDark ? DEFAULT_LOGO_DARK_HREF : DEFAULT_LOGO_LIGHT_HREF

    if (!branding) return { logoUrl: fallback, fallbackUrl: fallback }

    const settingsUrl = useDark
      ? branding.logoDarkUrl || (surface === 'footer' ? null : branding.logoUrl)
      : branding.logoUrl

    return { logoUrl: settingsUrl || fallback, fallbackUrl: fallback }
  }, [branding, getEffectiveMode, surface])

  return { logoUrl, fallbackUrl, isLoading }
}

/** Use a settings/custom logo, and swap to the default if the image fails to load. */
export function useLogoImageSrc(preferredUrl: string | null | undefined, fallbackUrl: string) {
  const resolvedFallback = fallbackUrl || DEFAULT_LOGO_LIGHT_HREF
  const [src, setSrc] = useState(preferredUrl || resolvedFallback)

  useEffect(() => {
    setSrc(preferredUrl || resolvedFallback)
  }, [preferredUrl, resolvedFallback])

  const onError = useCallback(() => {
    setSrc((current) => (current === resolvedFallback ? current : resolvedFallback))
  }, [resolvedFallback])

  return { src, onError }
}
